import type {
  Task,
  TaskLog,
  Repository,
  DashboardStats,
  ApiResponse,
  PaginatedResponse,
} from '@cowork-gen/core';

// Use relative path for local dev, full URL for production
const API_BASE = import.meta.env.DEV
  ? '/api'
  : 'https://cowork-gen-worker.joexu430.workers.dev/api';

class ApiClient {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `HTTP error ${response.status}`);
    }

    return data;
  }

  // Tasks

  async getTasks(params?: {
    page?: number;
    pageSize?: number;
    status?: string;
    repoId?: string;
  }): Promise<PaginatedResponse<Task>> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.pageSize) query.set('pageSize', String(params.pageSize));
    if (params?.status) query.set('status', params.status);
    if (params?.repoId) query.set('repoId', params.repoId);

    const queryString = query.toString();
    return this.request(`/tasks${queryString ? `?${queryString}` : ''}`);
  }

  async getTask(id: string): Promise<ApiResponse<Task>> {
    return this.request(`/tasks/${id}`);
  }

  async createTask(data: {
    repoId: string;
    storyId: string;
    branch?: string;
    maxIterations?: number;
  }): Promise<ApiResponse<Task>> {
    return this.request('/tasks', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async cancelTask(id: string): Promise<ApiResponse> {
    return this.request(`/tasks/${id}/cancel`, {
      method: 'POST',
    });
  }

  async deleteTask(id: string): Promise<ApiResponse> {
    return this.request(`/tasks/${id}`, {
      method: 'DELETE',
    });
  }

  async getTaskLogs(taskId: string): Promise<ApiResponse<TaskLog[]>> {
    return this.request(`/tasks/${taskId}/logs`);
  }

  // Repositories

  async getRepos(): Promise<ApiResponse<Repository[]>> {
    return this.request('/repos');
  }

  async createRepo(data: {
    githubUrl: string;
    webhookSecret?: string;
  }): Promise<ApiResponse<Repository>> {
    return this.request('/repos', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteRepo(id: string): Promise<ApiResponse> {
    return this.request(`/repos/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  // Stats

  async getStats(): Promise<ApiResponse<DashboardStats>> {
    return this.request('/stats');
  }

  // Health

  async healthCheck(): Promise<ApiResponse<{ status: string; timestamp: string }>> {
    return this.request('/health');
  }
}

export const api = new ApiClient();

// WebSocket connection for real-time updates
export class WebSocketClient {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private listeners: Map<string, Set<(data: unknown) => void>> = new Map();

  connect(): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      return;
    }

    const wsUrl = import.meta.env.DEV
      ? `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws`
      : 'wss://cowork-gen-worker.joexu430.workers.dev/ws';

    this.ws = new WebSocket(wsUrl);

    this.ws.onopen = () => {
      console.log('WebSocket connected');
      this.reconnectAttempts = 0;
    };

    this.ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data) as { type: string; payload: unknown };
        this.emit(message.type, message.payload);
      } catch (error) {
        console.error('WebSocket message parse error:', error);
      }
    };

    this.ws.onclose = () => {
      console.log('WebSocket disconnected');
      this.scheduleReconnect();
    };

    this.ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
  }

  disconnect(): void {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('Max reconnect attempts reached');
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);

    setTimeout(() => {
      console.log(`Reconnecting (attempt ${this.reconnectAttempts})...`);
      this.connect();
    }, delay);
  }

  on(event: string, callback: (data: unknown) => void): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);

    return () => {
      this.listeners.get(event)?.delete(callback);
    };
  }

  private emit(event: string, data: unknown): void {
    this.listeners.get(event)?.forEach((callback) => callback(data));
  }

  send(type: string, payload?: unknown): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type, payload }));
    }
  }
}

export const wsClient = new WebSocketClient();
